import React, { useState, useEffect, useCallback } from 'react';
import TransitionalModal from "../components/transitional";
import AddHomeWorkIcon from '@mui/icons-material/AddHomeWork';
import { BURL } from '../../../services/base';
import SmallLoader from '../../widgets/SmallLoader';
import useAPI from '../../../hooks/useAPI';
import {
    Alert, Button, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, FormControl, InputLabel,
    MenuItem,
    Select, Slide, Snackbar, TextField
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link, useParams } from 'react-router-dom';
import { RemoveRedEyeOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { formatDate, shortenText } from '../../../utils/common.utils';
import { useFormik } from 'formik';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import axios from 'axios';
import TenantHorizontalLinearStepper from '../components/tenantLinearStepper';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

const Tenants = () => {
    const [loading, setLoading] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);

    const [showModal, setShowModal] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const [success, setSuccess] = useState(null);
    const [tenants, setTenants] = useState([]);

    const [search, setSearch] = useState("");
    const [filterProperty, setFilterProperty] = useState("");
    const [filterUnit, setFilterUnit] = useState("");

    const [properties, setProperties] = useState([]);
    const [units, setUnits] = useState([]);

    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [tenantRoomId, setTenantRoomId] = useState(false);
    const [title, setTitle] = useState('');

    const [showToaster, setShowToaster] = useState(false);
    const [toasterType, setToasterType] = useState('success');
    const [message, setMessage] = useState(null);

    const { POST } = useAPI();
    const { deleteRequest } = useAPI();
    const { unitId, id } = useParams();

    // Load Property List
    const loadProperties = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${BURL}admin/properties`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProperties(res.data.data || []);
        } catch (err) {
            console.log("Error loading properties");
        }
    };

    // Load Units List
    const loadUnits = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${BURL}admin/units`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUnits(res.data.data || []);
        } catch (err) {
            console.log("Error loading units");
        }
    };

    useEffect(() => {
        loadProperties();
        loadUnits();
    }, []);

    // Fetch Tenants API Call
    const getTenantsByUnitRoom = useCallback(async () => {
        const token = localStorage.getItem('token');
        setLoading(true);
        setErrorText(null);

        try {
            // Build params conditionally
            const params = {
                page,
                limit,
                propertyId: filterProperty || id,
                unitId: filterUnit || unitId,
            };

            if (search.trim() !== "") {
                params.search = search.trim();
            }

            const response = await axios.get(`${BURL}admin/tenants`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            const data = response.data;
            setTenants(data.data || []);
            setTotal(data.count || 0);

        } catch (err) {
            setErrorText("Failed to load tenants");
        }

        setLoading(false);
    }, [page, limit, search, filterProperty, filterUnit, id, unitId]);

    // Debounce fetching
    useEffect(() => {
        const delay = setTimeout(() => {
            getTenantsByUnitRoom();
        }, 400);

        return () => clearTimeout(delay);
    }, [search, filterProperty, filterUnit, page]);

    const handleToasterClose = () => setShowToaster(false);

    const handleConfirmation = (id, title) => {
        setTitle(title);
        setConfirmationOpen(true);
        setTenantRoomId(id);
    };

    const removeTenant = async () => {
        const token = localStorage.getItem('token');
        setConfirmLoading(true);
        setErrorText(null);

        try {
            const url = `${BURL}admin/tenant-room`;
            const body = { tenantRoomId };
            const response = await deleteRequest(url, body, token);

            setShowToaster(true);
            setToasterType(response.status ? "success" : "error");
            setMessage(response.message);

            getTenantsByUnitRoom();
        } catch (err) {
            setShowToaster(true);
            setToasterType("error");
            setMessage(err.message || "Failed to remove tenant");
        } finally {
            setConfirmLoading(false);
            setConfirmationOpen(false);
        }
    };

    const loader = <SmallLoader />;

    return (
        <>
            <div className='w-[100%] bg-white py-8 overflow-x-hidden'>
                <div className="px-[1.22rem] md:px-4 xl:px-10">

                    {/* HEADER */}
                    <div className='flex justify-between items-center'>
                        <p className='mb-[3rem] xl:mb-[5rem] font-semibold text-[16px] text-austel-green'>
                            Tenants Details
                        </p>
                        <button
                            className='px-5 py-2 bg-austel-green hover:bg-[#0d964d] flex items-center gap-2 rounded-full text-white'
                            onClick={() => setShowModal(true)}
                        >
                            <AddHomeWorkIcon className='text-white' />
                            <p className='font-bold'>Add Tenant</p>
                        </button>
                    </div>

                    <Dialog
                        open={showModal}
                        TransitionComponent={Transition}
                        keepMounted
                        onClose={() => setShowModal(false)}
                        aria-describedby="alert-dialog-slide-description"
                    >
                        <DialogContent>
                            <DialogContentText id="alert-dialog-slide-description">
                                <TenantHorizontalLinearStepper open={showModal} setOpen={setShowModal} refetchTenants={getTenantsByUnitRoom} />
                            </DialogContentText>
                        </DialogContent>
                    </Dialog>

                    {/* SEARCH + FILTERS */}
                    <div className="flex flex-col md:flex-row gap-4 mb-6">

                        {/* Search */}
                        <TextField
                            label="Search tenants..."
                            fullWidth
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                        />

                        {/* Filter Property */}
                        <FormControl fullWidth>
                            <InputLabel>Filter by Property</InputLabel>
                            <Select
                                label="Filter by Property"
                                value={filterProperty}
                                onChange={(e) => {
                                    setFilterProperty(e.target.value);
                                    setPage(1);
                                }}
                                className='!capitalize'
                            >
                                <MenuItem value="">All</MenuItem>

                                {properties.map((p) => (
                                    <MenuItem className='!capitalize' key={p.id} value={p.property.id}>
                                        {p.property.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Filter Unit */}
                        <FormControl fullWidth>
                            <InputLabel>Filter by Unit</InputLabel>
                            <Select
                                label="Filter by Unit"
                                value={filterUnit}
                                onChange={(e) => {
                                    setFilterUnit(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <MenuItem value="">All</MenuItem>

                                {units.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {u.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                    </div>

                    {/* TABLE */}
                    {loading && <SmallLoader />}
                    {(!loading && !tenants.length) && "No tenants found"}

                    {!loading && tenants.length !== 0 &&
                        <div className='overflow-x-auto my-table w-full'>
                            <table className='table-auto w-full text-[#6C6C6C]'>
                                <thead>
                                    <tr>
                                        <th>Room No</th>
                                        <th>Property</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Check In</th>
                                        <th>Check Out</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {tenants.map((tenant) =>
                                        <tr className='border-b'>
                                            <td className='font-bold text-[#0FA958] capitalize'>{tenant?.tenancyHistory[0]?.room?.tag}</td>
                                            <td className='capitalize'>{shortenText(tenant?.tenancyHistory[0]?.room?.unit?.property?.name, 20)}</td>
                                            <td className='capitalize'>{tenant?.user.firstname} {tenant?.user.lastname}</td>
                                            <td>{shortenText(tenant.user.email, 15)}</td>
                                            <td>{formatDate(tenant?.tenancyHistory[0]?.checkIn)}</td>
                                            <td>{formatDate(tenant?.tenancyHistory[0]?.checkOut)}</td>

                                            <td className='flex gap-4 items-center'>
                                                <Link to={`/dashboard/tenant/${tenant?.tenantId}`}>
                                                    <RemoveRedEyeOutlined className='cursor-pointer' />
                                                </Link>

                                                <DeleteIcon
                                                    onClick={() => handleConfirmation(tenant?.tenancyHistory[0]?.tenantRoomId, 'remove_tenant')}
                                                    className='cursor-pointer text-red-600'
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    }

                    {/* PAGINATION */}
                    {!loading && tenants.length > 0 && (
                        <div className="flex justify-center my-6">
                            <Stack spacing={2}>
                                <Pagination
                                    count={Math.ceil(total / limit)}
                                    page={page}
                                    onChange={(e, newPage) => setPage(newPage)}
                                    color="primary"
                                    size="large"
                                />
                            </Stack>
                        </div>
                    )}

                </div>

                {/* DELETE CONFIRMATION */}
                <Dialog
                    open={confirmationOpen}
                    onClose={() => setConfirmationOpen(false)}
                >
                    <DialogTitle>Remove Tenant?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to remove this tenant?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmationOpen(false)}>No</Button>

                        <Button sx={{ color: 'red' }} onClick={removeTenant}>
                            {!confirmLoading ? "Yes" : loader}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={showToaster}
                    autoHideDuration={5000}
                    onClose={handleToasterClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={toasterType} onClose={handleToasterClose}>
                        {message}
                    </Alert>
                </Snackbar>
            </div>
        </>
    );
};

export default Tenants;